/**
 * Dónde está tu dinero, y cómo llegó hasta ahí.
 *
 * Son los dos gráficos de Cuentify que faltaban: «Distribución de Activos por Cuenta» y
 * «Trayectoria de Saldo Acumulado».
 *
 * ---------------------------------------------------------------------------
 *  Dos decisiones que hacen que estos gráficos no mientan
 * ---------------------------------------------------------------------------
 *
 * **1. Las deudas no son activos.** El reparto por cuenta enseña solo las cuentas donde
 * hay dinero a favor. Meter una tarjeta de crédito con saldo negativo en un gráfico
 * llamado «dónde está tu dinero» sería confuso: no es dinero tuyo, es dinero que debes.
 * Las deudas tienen su propia tarjeta, con su propio nombre.
 *
 * **2. La trayectoria termina en el saldo real de hoy.** El error fácil aquí es sumar
 * los flujos de los meses que se ven y llamarlo «saldo acumulado». Eso no es el saldo:
 * es **cuánto ha cambiado** en ese tramo, y si la aplicación no existía antes, la línea
 * empezaría en cero y subiría como si el dinero hubiera aparecido de la nada.
 *
 * Lo que se hace es lo contrario: se parte del dinero que hay **hoy** y se va hacia
 * atrás restando lo que entró y salió cada mes. Así la línea termina en la cifra real
 * que el usuario ve en su cuenta, y el pasado que se dibuja es coherente con el presente.
 */

import { useQuery } from '@tanstack/react-query';

import { formatCOP, formatMonthShort, isAssetAccount } from '@eldescuadre/core';

import { getAccounts, getSeries, getSummary } from '../lib/endpoints';
import { balanceTrajectory } from '../lib/trajectory';
import { DonutChart, LineChart } from './charts';
import { Card, Loading, Note } from './ui';

/**
 * Reparto del dinero entre las cuentas.
 *
 * Va en el panel principal, junto a «Tu situación», porque es la respuesta a la
 * pregunta que se hace al abrir la aplicación: ¿dónde tengo la plata?
 */
export function AssetDistribution({ monthKey }: { monthKey: string }): React.JSX.Element {
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => getAccounts() });
  const summary = useQuery({
    queryKey: ['summary', monthKey],
    queryFn: () => getSummary(monthKey),
  });

  if (accounts.isLoading) return <Loading />;
  if (accounts.isError || !accounts.data) return <></>;

  // Solo las cuentas donde hay dinero a favor. Las deudas tienen su propia tarjeta.
  const withMoney = accounts.data.accounts
    .filter((account) => !account.isArchived && isAssetAccount(account.kind) && account.balance > 0)
    .sort((a, b) => b.balance - a.balance);

  const total = withMoney.reduce((acc, account) => acc + account.balance, 0);
  const liabilities = summary.data?.liabilities ?? 0;

  if (withMoney.length === 0) {
    return (
      <Card title="Dónde está tu dinero">
        <p className="text-sm text-ed-text-muted">
          No hay dinero en tus cuentas ahora mismo. Cuando registres un ingreso, aquí verás
          cómo se reparte.
        </p>
      </Card>
    );
  }

  return (
    <Card
      title="Dónde está tu dinero"
      action={
        <span className="text-xs text-ed-text-muted">
          {withMoney.length} {withMoney.length === 1 ? 'cuenta' : 'cuentas'}
        </span>
      }
    >
      <DonutChart
        data={withMoney.map((account) => ({
          label: account.name,
          value: account.balance,
          color: account.color,
        }))}
        centerLabel="En cuentas"
      />

      {/* La resta que de verdad importa, y que no se ve en el gráfico: lo que tienes
          menos lo que debes. Un reparto de activos sin esto puede dar una sensación
          falsa de holgura. */}
      {liabilities > 0 && (
        <div className="mt-4 space-y-1.5 border-t border-ed-bg-border/30 pt-3 text-sm">
          <div className="flex justify-between">
            <span className="text-ed-text-muted">Tienes en cuentas</span>
            <span className="ed-money font-semibold text-ed-success">{formatCOP(total)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ed-text-muted">Debes</span>
            <span className="ed-money font-semibold text-ed-coral-light">
              {formatCOP(liabilities)}
            </span>
          </div>
          <div className="flex justify-between border-t border-ed-bg-border/30 pt-1.5">
            <span className="font-medium">Te queda de verdad</span>
            <span className="ed-money font-bold">{formatCOP(total - liabilities)}</span>
          </div>
        </div>
      )}

      {liabilities === 0 && (
        <p className="mt-3 border-t border-ed-bg-border/30 pt-3 text-sm text-ed-text-muted">
          No tienes deudas registradas, así que todo esto es tuyo.
        </p>
      )}
    </Card>
  );
}

/**
 * Cómo ha llegado el saldo hasta hoy.
 *
 * Va en Informes, y usa doce meses: con menos no se ve una trayectoria, se ven dos
 * puntos.
 */
export function BalanceTrajectory({ monthKey }: { monthKey: string }): React.JSX.Element {
  const series = useQuery({
    queryKey: ['reports', 'series', 12, monthKey],
    queryFn: () => getSeries(12, monthKey),
  });
  const summary = useQuery({
    queryKey: ['summary', monthKey],
    queryFn: () => getSummary(monthKey),
  });

  if (series.isLoading || summary.isLoading) return <Loading />;
  if (!series.data || !summary.data) return <></>;

  // El cálculo vive en `lib/trajectory.ts`, que está probado aparte. Aquí solo se
  // dibuja: mezclar las dos cosas es cómo se cuelan los errores de cuentas.
  const { points, startingBalance, change } = balanceTrajectory(
    series.data.series.map((point) => ({ monthKey: point.monthKey, net: point.net })),
    summary.data.availableCash,
  );

  const data = points.map((point) => ({
    label: formatMonthShort(point.monthKey),
    value: point.balance,
  }));

  // Se avisa cuando el saldo venía de números rojos: salir de ahí no es lo mismo que
  // ahorrar, y leerlo igual cambia por completo lo que significa la subida.
  const startedNegative = startingBalance < 0;

  return (
    <Card title="Cómo llegó tu saldo hasta aquí">
      <LineChart
        data={data}
        label="Saldo en cuentas"
        emptyMessage="Todavía no hay suficientes meses para dibujar una trayectoria."
      />

      <p className="mt-3 text-sm">
        {change > 0 ? (
          <>
            En estos doce meses tu saldo ha subido{' '}
            <strong className="text-ed-success">{formatCOP(change)}</strong>.
          </>
        ) : change < 0 ? (
          <>
            En estos doce meses tu saldo ha bajado{' '}
            <strong className="text-ed-coral-light">{formatCOP(Math.abs(change))}</strong>.
          </>
        ) : (
          <>Tu saldo está igual que hace doce meses.</>
        )}
      </p>

      {startedNegative && (
        <p className="mt-1 text-xs text-ed-text-muted">
          Hace doce meses tenías el saldo en rojo ({formatCOP(Math.abs(startingBalance))} debajo
          de cero). Salir de ahí también cuenta como subir.
        </p>
      )}

      <div className="mt-3">
        <Note>
          La línea termina en el dinero que tienes <strong>hoy</strong> y se calcula hacia
          atrás con lo que entró y salió cada mes. No es una estimación: es el saldo real
          deshaciendo los movimientos.
        </Note>
      </div>
    </Card>
  );
}
